import React from 'react';
import { Calendar, Heart, Sparkles, Sun, Crown, ArrowRight } from 'lucide-react';
import { danaSchedule } from '../data/mock';

const DanaSchedule = () => {
  const availableSlots = danaSchedule.filter(slot => slot.donor === 'Available').length;
  const bookedSlots = danaSchedule.filter(slot => slot.donor !== 'Available').length;

  const monthName = new Date().toLocaleString('default', { month: 'long' });
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/36357482/pexels-photo-36357482.jpeg)',
          }}
        />
        {/* dark gradient veil */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        {/* gold glow */}
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-amber-500/15 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 text-amber-700/90 tracking-[0.28em] text-xs uppercase mb-6">
            <span className="h-px w-10 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Generosity · Dāna Pāramī</span>
          </div>

          <h1
            data-testid="dana-hero-title"
            className="font-serif text-5xl md:text-7xl leading-[1.05] tracking-tight text-stone-900 max-w-4xl"
            style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
          >
            Daily Dāna<br />
            <span className="italic text-amber-800/95">Schedule</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
            A living calendar of devotion. Each day of the month is offered by a heart
            committed to sustaining the Sangha and the silent unfolding of the Dhamma.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <a
              href="/contact"
              data-testid="dana-hero-reserve-btn"
              className="group inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-600 text-black px-7 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all"
            >
              Reserve a Day
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="/donate"
              className="text-stone-800 hover:text-amber-800 text-sm tracking-wide underline-offset-4 hover:underline transition-colors"
            >
              Or contribute to the temple →
            </a>
          </div>

          {/* hero stats strip */}
          <div className="mt-16 grid grid-cols-3 max-w-3xl border-t border-stone-300/60 pt-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-600 mb-2">Month</div>
              <div className="text-2xl font-serif text-amber-800">{monthName} {year}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-600 mb-2">Sponsored</div>
              <div className="text-2xl font-serif text-amber-800">{bookedSlots} days</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-600 mb-2">Open</div>
              <div className="text-2xl font-serif text-amber-800">{availableSlots} days</div>
            </div>
          </div>
        </div>
      </section>

      {/* MONTHLY DONORS NOTE */}
      <section className="relative max-w-6xl mx-auto px-6 lg:px-10 -mt-6">
        <div
          data-testid="dana-monthly-donors-card"
          className="relative rounded-2xl p-8 md:p-10 overflow-hidden border border-amber-500/40"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,251,235,0.97) 0%, rgba(253,242,213,0.92) 100%)',
            backdropFilter: 'blur(14px)',
            boxShadow:
              '0 1px 0 rgba(255,255,255,0.7) inset, 0 14px 40px -18px rgba(120,80,20,0.18)',
          }}
        >
          <div className="absolute top-0 right-0 w-72 h-72 bg-amber-400/8 blur-3xl rounded-full" />
          <div className="relative flex flex-col md:flex-row gap-8 items-start">
            <div className="shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Crown size={24} className="text-black/80" />
            </div>
            <div className="flex-1">
              <div className="text-amber-700/80 text-xs tracking-[0.25em] uppercase mb-2">
                Devoted Monthly Donors
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-stone-900 mb-3">
                The pillars of our daily offering
              </h3>
              <p className="text-stone-700 leading-relaxed max-w-3xl">
                These devotees offer Dāna to the temple every month on their chosen day —
                a quiet, continuous current of generosity that holds the Sangha steady.
                We bow with deep gratitude.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CALENDAR */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-amber-700/80 text-xs tracking-[0.25em] uppercase mb-2">
              The Calendar
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-900">
              A day for every offering
            </h2>
          </div>
          <div className="flex items-center gap-5 text-xs text-stone-600">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-300 to-amber-500" />
              Sponsored
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full border border-stone-600" />
              Available
            </span>
          </div>
        </div>

        <div
          data-testid="dana-calendar-grid"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3 md:gap-4"
        >
          {danaSchedule.map((slot) => {
            const isAvailable = slot.donor === 'Available';
            return (
              <div
                key={slot.date}
                data-testid={`dana-day-${slot.date}`}
                className={`group relative rounded-xl p-4 md:p-5 min-h-[150px] flex flex-col justify-between transition-all duration-300 cursor-default
                  ${
                    isAvailable
                      ? 'bg-[#fbf5e3]/80 border border-stone-300/60 hover:border-amber-600/70 hover:bg-[#fbf5e3]/95 hover:-translate-y-0.5'
                      : 'border border-amber-500/40 hover:border-amber-600/80 hover:-translate-y-0.5'
                  }`}
                style={
                  !isAvailable
                    ? {
                        background:
                          'linear-gradient(160deg, rgba(245,224,172,0.95) 0%, rgba(228,196,128,0.85) 100%)',
                        boxShadow:
                          '0 1px 0 rgba(255,255,255,0.5) inset, 0 8px 24px -14px rgba(120,80,20,0.3)',
                      }
                    : undefined
                }
              >
                {/* date number */}
                <div className="flex items-start justify-between">
                  <span
                    className={`font-serif text-3xl md:text-4xl leading-none ${
                      isAvailable ? 'text-stone-500' : 'text-amber-900'
                    }`}
                  >
                    {slot.date}
                  </span>
                  {!isAvailable && (
                    <span className="text-amber-700/90">
                      <Sun size={14} />
                    </span>
                  )}
                </div>

                {/* footer */}
                <div className="mt-4">
                  {isAvailable ? (
                    <span className="text-[11px] uppercase tracking-widest text-stone-500 group-hover:text-amber-700 transition-colors">
                      Available
                    </span>
                  ) : (
                    <>
                      <div className="text-[10px] uppercase tracking-widest text-amber-700/70 mb-1">
                        Offered by
                      </div>
                      <div className="text-sm text-stone-900 font-medium leading-snug break-words">
                        {slot.donor}
                      </div>
                      {slot.isMonthly && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700/90">
                          <Sparkles size={10} /> Monthly
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="relative max-w-6xl mx-auto px-6 lg:px-10 pb-28">
        <div
          className="relative rounded-3xl overflow-hidden border border-amber-500/40 p-10 md:p-16"
          style={{
            background:
              'radial-gradient(circle at 20% 20%, rgba(220,170,80,0.35) 0%, transparent 55%), linear-gradient(135deg, #f9eecc 0%, #ecd9a8 100%)',
          }}
        >
          <div className="absolute -bottom-32 -right-20 w-[420px] h-[420px] rounded-full bg-amber-400/30 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-amber-700/80 text-xs tracking-[0.28em] uppercase mb-4">
                Sponsor a Day
              </div>
              <h3 className="font-serif text-4xl md:text-5xl text-stone-900 leading-tight mb-5">
                Offer the gift that<br />
                <span className="italic text-amber-800">sustains everything.</span>
              </h3>
              <p className="text-stone-700 leading-relaxed max-w-lg">
                Choose a date that holds meaning for you — a birthday, an anniversary,
                a remembrance — and let your Dāna ripple through the temple's daily life.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <a
                href="/contact"
                data-testid="dana-cta-reserve-btn"
                className="group inline-flex items-center justify-between bg-amber-600 hover:bg-amber-700 text-white px-7 py-5 rounded-2xl transition-all shadow-md shadow-amber-700/20"
              >
                <span className="flex items-center gap-3">
                  <Calendar size={20} />
                  <span className="text-base font-medium tracking-wide">Reserve a Date</span>
                </span>
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="/donate"
                data-testid="dana-cta-donate-btn"
                className="group inline-flex items-center justify-between bg-[#fbf5e3]/90 hover:bg-white text-stone-900 border border-stone-300 hover:border-amber-600/60 px-7 py-5 rounded-2xl transition-all"
              >
                <span className="flex items-center gap-3">
                  <Heart size={20} className="text-amber-700" />
                  <span className="text-base font-medium tracking-wide">Make a Donation</span>
                </span>
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-t border-amber-500/40 bg-[#d4c094]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-xs tracking-[0.28em] uppercase mb-3">
              The Threefold Fruit
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-900">
              Why we offer Dāna
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Cultivate Generosity',
                copy: 'Loosen the grip of clinging. Dāna is the first paramī — the doorway to a heart that gives freely.',
              },
              {
                icon: Crown,
                title: 'Sustain the Sangha',
                copy: "Your offering maintains the temple, supports the monks, and keeps the Dhamma freely available to all.",
              },
              {
                icon: Sparkles,
                title: 'Create Merit',
                copy: 'Wholesome action carries forward — a quiet inheritance of brightness for this life and beyond.',
              },
            ].map(({ icon: Icon, title, copy }) => (
              <div
                key={title}
                className="group p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/70 hover:bg-[#fbf5e3]/90 hover:border-amber-500/40 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-200 to-amber-500 flex items-center justify-center mb-6 shadow-lg shadow-amber-500/20">
                  <Icon size={22} className="text-black/80" />
                </div>
                <h3 className="font-serif text-2xl text-stone-900 mb-3">{title}</h3>
                <p className="text-stone-600 leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DanaSchedule;
